/**
 * workspaceTabsHelpers — pure helpers shared by Navigation + WorkspaceTabs
 * for opening tabs by path with friendly labels derived from navigationConfig.
 *
 * Spec: 2026-05-19-qfinpricer-portal-skin-qyas.md, day-4 follow-up
 */

import { navigationConfig, findSectionForPath, type NavigationItem } from './navigationConfig';
import type { WorkspaceTab } from './workspaceTabsStore';

/** Find the deepest navigation item whose path matches the route. */
export function findItemForPath(path: string): NavigationItem | undefined {
  let best: NavigationItem | undefined;
  let bestLen = 0;

  const visit = (items: NavigationItem[]) => {
    for (const item of items) {
      if ((path === item.path || path.startsWith(item.path + '/')) && item.path.length > bestLen) {
        best = item;
        bestLen = item.path.length;
      }
      if (item.children?.length) visit(item.children);
    }
  };

  for (const section of navigationConfig) visit(section.children);
  return best;
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

function titleCase(seg: string): string {
  return seg.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Friendly label for an arbitrary route — uses navigationConfig when possible, falls back to a path segment.
 *  E.g. '/alm/eve-analysis' → 'EVE Analysis'.
 *  For unmapped routes (deep links, detail pages by id), walks up the path
 *  until a nav match is found and adds a suffix (Details / Edit / New) based
 *  on the trailing segment. */
export function labelForPath(path: string): string {
  if (path === '/' || path === '/home') return 'Home';
  const item = findItemForPath(path);
  if (item) return item.label;

  const segs = path.split('/').filter(Boolean);
  const last = segs[segs.length - 1] ?? '';
  let suffix = '';
  let trimmed = segs;
  if (last === 'edit' || last === 'new') {
    suffix = titleCase(last);
    trimmed = segs.slice(0, -1);
  }
  // If the (possibly trimmed) tail is a UUID, treat as a detail page.
  if (trimmed.length && UUID_RE.test(trimmed[trimmed.length - 1])) {
    if (!suffix) suffix = 'Details';
    trimmed = trimmed.slice(0, -1);
  }

  // Walk up the trimmed segments until we find a nav match.
  for (let i = trimmed.length; i > 0; i--) {
    const prefix = '/' + trimmed.slice(0, i).join('/');
    const match = findItemForPath(prefix);
    if (match) {
      return suffix ? `${match.label} · ${suffix}` : match.label;
    }
  }

  // Last resort — title-case the deepest non-uuid segment.
  const fallbackSeg = trimmed[trimmed.length - 1] ?? last;
  const base = titleCase(fallbackSeg);
  return suffix ? `${base} · ${suffix}` : base;
}

/** Build a SECTION tab — the default kind, anchored to the owning section.
 *  Used for in-tab navigation and default tab creation when landing on a
 *  route via the URL bar / a regular click. */
export function buildSectionTabForPath(path: string): WorkspaceTab {
  if (path === '/' || path === '/home') {
    return { id: 'home', kind: 'home', label: 'Home', path: '/', closeable: false };
  }
  const section = findSectionForPath(path);
  if (section) {
    return {
      id: `section:${section.id}`,
      kind: 'section',
      label: section.label,
      path,
      closeable: true,
    };
  }
  // No section owns this path — fall back to a page tab.
  return buildPageTab(path);
}

/** Build a PAGE tab — pinned to a single path, opened via the explicit
 *  Cmd/Ctrl/middle-click gesture on a sidebar leaf item. */
export function buildPageTab(path: string): WorkspaceTab {
  return {
    id: `page:${path}`,
    kind: 'page',
    label: labelForPath(path),
    path,
    closeable: true,
  };
}

/** Back-compat alias — kept so existing call-sites compile.
 *  Resolves to a section tab (the new default). */
export function buildTabForPath(path: string): WorkspaceTab {
  return buildSectionTabForPath(path);
}

/** Did the user trigger an "open in new tab" gesture? */
export function isOpenInNewTabGesture(e: { metaKey: boolean; ctrlKey: boolean; button?: number }): boolean {
  return Boolean(e.metaKey || e.ctrlKey || e.button === 1);
}
